import React from "react";
import { Box } from "@mui/material";
import { COLORS } from "../../../assets/theme";
import CustomTable from "../../../components/table/CustomTable";
import SearchHeaderContainer from "../../../components/table/SearchHeaderContainer";
import PageHeader from "../../../components/PageHeader";
import { getPayments } from "../../../services/newPaymentsService";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

const Payments: React.FC = () => {
  const [rows, setRows] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [provider, setProvider] = React.useState<string>("all");
  const [dateRange, setDateRange] = React.useState<{
    start?: string;
    end?: string;
  } | null>(() => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const today = `${yyyy}-${mm}-${dd}`;
    return { start: today, end: today };
  });

  React.useEffect(() => {
    const fetchPayments = async () => {
      setLoading(true);
      try {
        const params: any = {};
        if (provider !== "all") params.provider = provider;

        if (dateRange) {
          params.from = dateRange.start;
          params.to = dateRange.end;
        }

        const data = await getPayments(params);
        setRows(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error(e);
        setRows([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPayments();
  }, [provider, dateRange]);

  const exportCsv = React.useCallback(() => {
    if (!rows || rows.length === 0) return;
    const keys = ["invoiceId", "amount", "method", "provider", "status", "createdAt"];
    const header = keys.join(",");
    const lines = rows.map((r: any) => {
      const row: Record<string, any> = {
        invoiceId: r.invoiceId ?? "-",
        amount: r.amount ?? "-",
        method: r.method ?? "-",
        provider: r.provider ?? "-",
        status: r.status ?? "-",
        createdAt: r.createdAt ?? "-",
      };
      return keys
        .map((k) => `"${String(row[k] ?? "-").replace(/"/g, '""')}"`)
        .join(",");
    });

    const csv = [header, ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `payments-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [rows]);

  return (
    <Box sx={{ width: "100%", maxWidth: "90vw" }}>
      <PageHeader
        title="Payments"
        subtitle="Payments received, by provider."
        sx={{ mt: { xs: 2, sm: 3 } }}
      />

      <SearchHeaderContainer
        search={search}
        setSearch={setSearch}
        selectConfig={{
          label: "Provider",
          value: provider,
          onChange: (v: any) => setProvider(v),
          options: [
            { label: "All", value: "all" },
            { label: "Stripe", value: "STRIPE" },
            { label: "Tap", value: "TAP" },
            { label: "PayPal", value: "PAYPAL" },
            { label: "Manual", value: "MANUAL" },
          ],
        }}
        dateRangeConfig={{
          value: dateRange ?? undefined,
          onChange: (v: any) => setDateRange(v),
        }}
        buttonConfig={[
          {
            label: "Export CSV",
            onClick: () => Promise.resolve(exportCsv()),
            startIcon: <FileDownloadIcon />,
            variant: "contained",
            bgColor: COLORS.MainColor,
            textColor: "#fff",
          },
        ]}
      />

      <CustomTable<any>
        filters={{ page: 0, size: 10, search }}
        loading={loading}
        clientSidePagination={true}
        fields={[
          { key: "invoiceId", header: "Invoice" },
          { key: "amount", header: "Amount" },
          { key: "method", header: "Method" },
          { key: "provider", header: "Provider" },
          { key: "status", header: "Status" },
          { key: "createdAt", header: "Date" },
        ]}
        fetchFunc={async (params?: any) => {
          const q = String(params?.search || search || "")
            .toLowerCase()
            .trim();

          // A number ("60", "$60", "#2497") is an exact amount or invoice
          // number; substring matching would turn "60" into 160, 600, #2601.
          const numeric = q.replace(/^[#$]/, "");
          const asNumber = /^\d+(\.\d+)?$/.test(numeric) ? Number(numeric) : null;

          const filtered = rows.filter((r) => {
            if (!q) return true;
            if (asNumber !== null) {
              return (
                Number(r.amount) === asNumber ||
                Number(r.invoiceId) === asNumber
              );
            }
            return (
              String(r.provider).toLowerCase().includes(q) ||
              String(r.method).toLowerCase().includes(q) ||
              String(r.status).toLowerCase().includes(q)
            );
          });

          return {
            content: filtered,
            number: 0,
            size: filtered.length,
            totalElements: filtered.length,
            totalPages: 1,
          };
        }}
      />
    </Box>
  );
};

export default Payments;
